// src/components/inputs/SqrtInput.js
import React, { useState, useEffect, useRef } from "react";
import { InlineMath } from "react-katex";
import SetKeyboard from "./SetKeyboard";
import { normalizePreviewLabel } from "./previewLabel";
import { numericMathExpression, previewMathExpression } from "./mathExpression";

/* Te same stałe co w FractionInput */
const INDENT = 36;               // px – kolumna na ikonkę + odstęp
const PANEL_WIDTH_DESKTOP = 250; // px – szerokość panelu (bez INDENT)

/* =================== Helpers =================== */

// Minimalny formatter do podglądu KaTeX.
// Obsługa:
//   √12 -> \sqrt{12}, 2√3 -> 2\sqrt{3}
//   ∛8  -> \sqrt[3]{8}, 2∛3 -> 2\sqrt[3]{3}
// Obsługuje też \sqrt{..}, sqrt(..), \sqrt[3]{..}, sqrt[3](..)
function toLatexPreview(raw) { return previewMathExpression(raw); }
export function parseSqrtLikeToNumber(raw) { return numericMathExpression(raw); }

export function getSqrtInputNumericValue(value) {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (value && typeof value === "object" && Number.isFinite(value.parsed)) {
    return value.parsed;
  }

  if (value && typeof value === "object") {
    for (const key of ["raw", "latex", "text", "value"]) {
      if (typeof value[key] === "string") {
        const parsed = parseSqrtLikeToNumber(value[key]);
        if (Number.isFinite(parsed)) return parsed;
      }
    }
    return null;
  }

  return parseSqrtLikeToNumber(value);
}

export default function SqrtInput({
  value = "",
  onChange,
  disabled,
  wide,
  panelWidthDesktop = PANEL_WIDTH_DESKTOP,
  indentPx = INDENT,
  allowCubeRoot = false, // pokazuj ∛
  onlyCubeRoot = false,  // pokazuj tylko ∛ (bez √)

  // ✅ nowe (jak w FractionInput)
  label: inputLabel = "",            // np. "d" albo "x"
  suffixLatex = "",      // np. "\\text{cm}" albo "\\text{zł}"
}) {
  const label = normalizePreviewLabel(inputLabel);
  const pickString = (v) =>
    v && typeof v === "object" && "raw" in v ? (v.raw ?? "") : (v ?? "");

  const [local, setLocal] = useState(String(pickString(value) ?? ""));
  const [kbdOpen, setKbdOpen] = useState(false);
  const inputRef = useRef(null);

  const cleanup = (s) =>
    String(s ?? "")
      .replace(/\u200b/g, "")
      .replace(/[\u0000-\u001F\u007F-\u009F]/g, "")
      .replace(/,/g, ".")
      .replace(/[−–]/g, "-")
      .replace(/\\\\sqrt\s*\[\s*3\s*\]/gi, "∛")
      .replace(/\\sqrt\s*\[\s*3\s*\]/gi, "∛")
      .replace(/sqrt\s*\[\s*3\s*\]/gi, "∛")
      .replace(/\\\\sqrt/gi, "√")
      .replace(/\\sqrt/gi, "√")
      .replace(/sqrt/gi, "√");

  useEffect(() => {
    setLocal(String(pickString(value) ?? ""));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const updateLocal = (text) => { setLocal(text); const raw = cleanup(text); onChange?.({raw, parsed: parseSqrtLikeToNumber(raw)}); };

  const setCaret = (pos) => {
    const el = inputRef.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(pos, pos);
  };

  const insertAtCursor = (text) => {
    const el = inputRef.current;
    if (!el) return;
    const { selectionStart: s = 0, selectionEnd: e = 0 } = el;
    const next = local.slice(0, s) + text + local.slice(e);
    updateLocal(next);
    setTimeout(() => setCaret(s + String(text).length), 0);
  };

  const handleKbdAction = (type, payload) => {
      const el = inputRef.current;
      if (!el) return;
      const s = el.selectionStart ?? 0;
      const e = el.selectionEnd ?? 0;

      if (type === "insert") {
        insertAtCursor(String(payload ?? ""));
        return;
      }
      if (type === "backspace") {
        if (s !== e) {
          const next = local.slice(0, s) + local.slice(e);
          updateLocal(next);
          setTimeout(() => setCaret(s), 0);
        } else if (s > 0) {
          const next = local.slice(0, s - 1) + local.slice(e);
          updateLocal(next);
          setTimeout(() => setCaret(s - 1), 0);
        }
        return;
      }
      if (type === "delete") {
        if (s !== e) {
          const next = local.slice(0, s) + local.slice(e);
          updateLocal(next);
          setTimeout(() => setCaret(s), 0);
        } else if (e < local.length) {
          const next = local.slice(0, s) + local.slice(e + 1);
          updateLocal(next);
          setTimeout(() => setCaret(s), 0);
        }
        return;
      }
      if (type === "moveLeft") {
        setTimeout(() => setCaret(Math.max(0, Math.min(s, e) - 1)), 0);
        return;
      }
      if (type === "moveRight") {
        setTimeout(() => setCaret(Math.min(local.length, Math.max(s, e) + 1)), 0);
        return;
      }
      if (type === "clear") {
        updateLocal("");
        setTimeout(() => setCaret(0), 0);
        return;
      }
      if (type === "enter") return;
      if (type === "toggle") {
        setKbdOpen((v) => !v);
        return;
      }
    };

  const insertSqrt = () => insertAtCursor("√");
  const insertCbrt = () => insertAtCursor("∛");

  const containerStyle = {
    width: "100%",
    maxWidth: panelWidthDesktop + indentPx,
    display: "flex",
    flexDirection: "column",
    gap: 8,
    alignItems: "flex-start",
  };

  const inputStyle = {
    width: "100%",
    minWidth: 0,
    flex: "0 0 auto",
  };

  const previewCore = toLatexPreview(local);

  // ✅ format podglądu jak w FractionInput: label = ... + suffixLatex
  const previewLatex = previewCore
    ? `${label ? `${label} = ` : ""}${previewCore}${suffixLatex ? ` \\, ${suffixLatex}` : ""}`
    : "";

  const placeholder =
    onlyCubeRoot
      ? "np. ∛8, 2∛3"
      : allowCubeRoot
        ? "np. √2, 2√3, ∛8, 2∛3"
        : "np. √2, 2√3";

  return (
    <div style={containerStyle}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: `${indentPx}px 1fr`,
          columnGap: 0,
          alignItems: "center",
          width: "100%",
        }}
      >
        <SetKeyboard
          render="toggle"
          targetRef={inputRef}
          open={kbdOpen}
          onOpenChange={setKbdOpen}
          onAction={handleKbdAction}
          compact
        />

        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            width: "100%",
          }}
        >
          <input
            ref={inputRef}
            type="text"
            value={local}
            onChange={(e) => updateLocal(e.target.value)}
            disabled={disabled}
            placeholder={placeholder}
            className="input-secondary"
            style={inputStyle}
          />

          {!onlyCubeRoot ? (
            <button
              type="button"
              onClick={insertSqrt}
              disabled={disabled}
              className="btn-secondary"
              aria-label="Wstaw √"
              title="Wstaw √"
            >
              √
            </button>
          ) : null}

          {allowCubeRoot ? (
            <button
              type="button"
              onClick={insertCbrt}
              disabled={disabled}
              className="btn-secondary"
              aria-label="Wstaw ∛"
              title="Wstaw ∛"
            >
              ∛
            </button>
          ) : null}
        </div>
      </div>

      {kbdOpen && (
        <div style={{ width: "100%" }}>
          <SetKeyboard
            render="panel"
            targetRef={inputRef}
            open={kbdOpen}
            onOpenChange={setKbdOpen}
            onAction={handleKbdAction}
            compact
            panelIndent={indentPx}
          />
        </div>
      )}

      {previewLatex ? (
        <div style={{ marginTop: 6, marginBottom: 0, pointerEvents: "none" }}>
          <strong>Podgląd:</strong> <InlineMath math={previewLatex} />
        </div>
      ) : null}
    </div>
  );
}
